import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { ArrowLeft } from "lucide-react";
import ShowPass from "../images/svg/ShowPass.svg";
import HidePass from "../images/svg/HidePass.svg";
import { APIUtility } from "../Api";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../Redux/CommonSlice/allDataSlice";
import { addToast } from "../Redux/CommonSlice/toastSlice";
import Spinner from "react-bootstrap/Spinner";
import { useForm } from "react-hook-form";

const AdduserModal = ({ show, onHide, isEditUser, EditData, setIsEditUser, fetchUsers, }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ErrorMessage, setErrorMessage] = useState(null);
  const dispatch = useDispatch();

  const userRoles = useSelector((state) => state.data.userRoles);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
    watch
  } = useForm({
    mode: "onChange",
    defaultValues: {
      username: "",
      password: "",
      role: ""
    }
  });

  const handleAddUser = async (formData) => {
    setIsLoading(true);

    try {
      let Data = {
        username: formData.username,
        password: formData.password,
        role: formData.role,
      };
      if (isEditUser) {
        Data["id"] = EditData?.id;
        const response = await APIUtility.UpdateNewUser(Data);

        if (response.status === 200) {
          setIsLoading(false);
          fetchUsers();
          onHide();
          dispatch(
            addToast({ message: "User Updated Successfully", isError: false })
          );
        } else if (response.status === 400) {
          const errorMessage = response?.data?.username;
          setErrorMessage(errorMessage);
          setIsLoading(false);
          dispatch(addToast({ message: "Failed to edit user", isError: true }));
        }
      } else {
        const response = await APIUtility.AddNewUser(Data);

        if (response.status === 201) {
          setIsLoading(false);
          fetchUsers();
          onHide();
          dispatch(
            addToast({ message: "User Added Successfully", isError: false })
          );
        } else if (response.status === 400) {
          setIsLoading(false);
          const errorMessage = response?.data?.username;
          setErrorMessage(errorMessage);
          dispatch(addToast({ message: "User Not Added", isError: true }));
        } else {
          setIsLoading(false);
          dispatch(addToast({ message: "Failed to Add user", isError: true }));
        }
      }
    } catch (error) {
      setIsLoading(false);

      console.error("Failed to add user:", error);
      dispatch(
        addToast({
          message: error.message || error.Text || "Failed to Add user",
          isError: true,
        })
      );
    }
  };

  useEffect(() => {
    if (isEditUser && EditData) {
      setValue("username", EditData?.username);
      setValue("password", EditData?.password);
      setValue("role", EditData?.role);
    } else {
      reset({
        username: "",
        password: "",
        role: userRoles && userRoles.length > 0 ? userRoles[0].display_role_name : ""
      });
    }
  }, [show, userRoles, isEditUser, EditData, setValue, reset]);

  return (
    <div>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        aria-labelledby="example-modal-sizes-title-lg"
        style={{ top: '9.5%' }}
      >
        <Modal.Body>
          <div className="p-2">
            <div className="flex items-center mb-1">
              <ArrowLeft
                className="mr-2 cursor-pointer mb-1.5"
                onClick={onHide}
              />
              <h2 className="text-xl font-medium">
                {isEditUser ? "Edit User Details" : "Add New User"}
              </h2>
            </div>

            <form onSubmit={handleSubmit(handleAddUser)}>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <label className="block mb-2" htmlFor="username">
                    User Email
                  </label>
                  <input
                    type="text"
                    id="username"
                    className={`border p-2 w-full rounded-md ${errors.username ? "border-red-500" : ""}`}
                    placeholder="Enter Email Address"
                    autoComplete="off"
                    {...register("username", {
                      required: "Username Is Required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please Enter A Valid Email Address"
                      },
                      onChange: () => setErrorMessage(null)
                    })}
                  />
                  <div className="h-2">
                    {errors.username && (
                      <p className="m-0 text-[0.8rem] whitespace-nowrap font-normal text-red-500">
                        {errors.username.message}
                      </p>
                    )}
                    {ErrorMessage && (
                      <p className="m-0 text-[0.8rem] whitespace-nowrap font-normal text-red-500">
                        {ErrorMessage}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-2" htmlFor="password">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      className={`border p-2 w-full pr-10 rounded-md ${errors.password ? "border-red-500" : ""}`}
                      placeholder="Enter Password"
                      autoComplete="off"
                      {...register("password", {
                        required: "Password Is Required",
                        minLength: {
                          value: 8,
                          message: "Password Must Be At Least 8 Characters"
                        }
                      })}
                    />
                    <img
                      src={showPassword ? ShowPass : HidePass}
                      alt="Toggle Visibility Icon"
                      className="bottom-[41%] cursor-pointer absolute right-2 top-3 w-5 h-5 text-gray-400"
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </div>
                  <div className="h-2">
                    {errors.password && (
                      <p className="m-0 text-[0.8rem] whitespace-nowrap font-normal text-red-500">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-2" htmlFor="role">
                    User Role
                  </label>
                  <select
                    id="role"
                    className={`border p-2 w-full text-black font-semibold rounded-md ${errors.role ? "border-red-500" : ""}`}
                    {...register("role", { required: "User Role Is Required" })}
                  >
                    {userRoles && userRoles.length > 0 &&
                      userRoles.map((role) => (
                        <option key={role.role_id} value={role.name}>
                          {role.display_role_name}
                        </option>
                      ))
                    }
                  </select>
                  <div className="h-2">
                    {errors.role && (
                      <p className="m-0 text-[0.8rem] whitespace-nowrap font-normal text-red-500">
                        {errors.role.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  type="button"
                  className="bg-customGreenButtonOne text-black font-semibold py-2 px-4 rounded-md"
                  onClick={onHide}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`py-2 px-4 rounded-md font-semibold ${isValid
                      ? "bg-customGreen text-white"
                      : "bg-customGreenButtonTwo text-white"
                    }`}
                  disabled={!isValid || isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      {isEditUser ? "Updating..." : "Adding..."}
                    </div>
                  ) : isEditUser ? (
                    "Update User"
                  ) : (
                    "Add User"
                  )}
                </button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdduserModal;